import Ajv from 'ajv';
import { z } from 'zod';
import { Education as EducationConst, Education } from '@axiom/const';
import { SchemaYear, ZodArrayToEnum } from '@axiom/types';

import { axiomValidationOptions } from './options';
import { AreaOfStudySchema } from './area-of-study';

const ajv = new Ajv(axiomValidationOptions());

export const EducationDegreesKeys = Object.keys(
  EducationConst.DEGREES
) as Array<keyof typeof EducationConst.DEGREES>;

export const EducationSchema = z.object({
  areaOfStudy: AreaOfStudySchema.nullish(),
  areaOfStudyId: z.string().uuid().nullish(),
  candidateId: z.string().uuid(),
  degree: z.enum(ZodArrayToEnum(EducationDegreesKeys)).nullable(),
  id: z.string().uuid(),
  institution: z.string().max(255),
  isPrimary: z.boolean(),
  lawSchoolId: z.string().uuid(),
  yearAwarded: SchemaYear.nullable(),
});

export type Education = z.infer<typeof EducationSchema>;

/**
 *  This fixes bug FR-6456
 *  The original schema used pick(). This let all the props to be their original required/nullable
 *  Doing schema.pick(schema.pick().required()); first pick() to get the nullable() props, the second pick() for required props,
 *  wasn't adding the needed asterisks to the label
 *  */
export const EducationEditSchema = z.object({
  areaOfStudyId: z.string().uuid().nullish(),
  candidateId: z.string().uuid(),
  lawSchoolId: z.string().uuid(),
  yearAwarded: SchemaYear.nullable(),
  degree: z.enum(ZodArrayToEnum(EducationDegreesKeys)),
  isPrimary: z.boolean().nullish(),
});

export type EducationEdit = z.infer<typeof EducationEditSchema>;

const educationCommonValidation = {
  type: 'object',
  additionalProperties: false,
  properties: {
    candidateId: {
      type: 'string',
      format: 'uuid',
    },
    degree: {
      type: 'string',
      enum: EducationDegreesKeys,
    },
    lawSchoolId: {
      type: 'string',
      format: 'uuid',
    },
    yearAwarded: {
      type: ['number', 'null'],
      minimum: 1950,
      maximum: new Date().getFullYear() + 10,
    },
    isPrimary: {
      type: 'boolean',
    },
    areaOfStudyId: {
      type: ['string', 'null'],
      format: 'uuid',
    },
  },
};

export const createEducationValidator = ajv.compile({
  ...educationCommonValidation,
  required: ['candidateId', 'degree', 'lawSchoolId', 'yearAwarded'],
});

export const updateEducationValidator = ajv.compile({
  ...educationCommonValidation,
  anyOf: [
    { required: ['degree'] },
    { required: ['lawSchoolId'] },
    { required: ['yearAwarded'] },
    { required: ['isPrimary'] },
    { required: ['areaOfStudyId'] },
  ],
});
